import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import api, { API_BASE_URL } from "../../utils/api";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const HOADocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [uploads, setUploads] = useState([]); // [{file, description}]
  const [editDocId, setEditDocId] = useState(null);
  const [newName, setNewName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const dropRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/owner-documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error("Failed to load documents", err);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);

    if (validFiles.length < files.length) {
      alert("Some files exceeded the 100MB limit and were ignored.");
    }

    const withDescriptions = validFiles.map(file => ({ file, description: "" }));
    setUploads(prev => [...prev, ...withDescriptions]);
  };

  const handleUpload = async () => {
    if (!uploads.length) return;
    const formData = new FormData();
    uploads.forEach((entry) => {
      formData.append("upload_urls", entry.file);
      formData.append("descriptions", entry.description);
    });

    try {
      await api.post(`/owner-documents`, formData);
      setUploads([]);
      fetchDocuments();
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/owner-documents/${id}`);
      fetchDocuments();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const handleEdit = (doc) => {
    setEditDocId(doc.id);
    setNewName(doc.original_filename);
    setEditDescription(doc.description || "");
  };

  const handleRename = async (id) => {
    try {
      await api.put(`/owner-documents/${id}`, {
        original_filename: newName,
        description: editDescription,
      });
      setEditDocId(null);
      setNewName("");
      setEditDescription("");
      fetchDocuments();
    } catch (err) {
      console.error("Rename failed", err);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        HOA Document Manager
      </Typography>

      {/* Drag and Drop Upload */}
      <Paper
        ref={dropRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleFileDrop}
        sx={{ p: 2, mb: 3, border: "2px dashed #ccc", textAlign: "center" }}
      >
        <Typography>Drag and drop files here, or select manually</Typography>
        <input
          type="file"
          multiple
          style={{ marginTop: 10 }}
          onChange={(e) => {
            const files = Array.from(e.target.files);
            const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);
            if (validFiles.length < files.length) {
              alert("Some files exceeded the 100MB limit and were ignored.");
            }
            const withDescriptions = validFiles.map(file => ({ file, description: "" }));
            setUploads(prev => [...prev, ...withDescriptions]);
          }}
        />
      </Paper>

      {/* Pending Upload List */}
      {uploads.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Files Ready for Upload</Typography>
          <List>
            {uploads.map((item, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={item.file.name}
                  secondary={`Size: ${(item.file.size / (1024 * 1024)).toFixed(2)} MB`}
                />
                <TextField
                  label="Description"
                  value={item.description}
                  onChange={(e) => {
                    const updated = [...uploads];
                    updated[index].description = e.target.value;
                    setUploads(updated);
                  }}
                  sx={{ ml: 2, flex: 1 }}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleUpload}
          >
            Upload All
          </Button>
        </Paper>
      )}

      {/* List of Existing Documents */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Uploaded Documents
        </Typography>
        {documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet.
          </Typography>
        ) : (
          <List>
            {documents.map((doc) => (
              <ListItem key={doc.id} divider>
                {editDocId === doc.id ? (
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      label="Filename"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ) : (
                  <ListItemText
                    primary={doc.original_filename}
                    secondary={`Description: ${doc.description || "N/A"} | Uploaded on ${new Date(
                      doc.upload_date
                    ).toLocaleDateString()}`}
                  />
                )}
                <ListItemSecondaryAction>
                  {editDocId === doc.id ? (
                    <IconButton onClick={() => handleRename(doc.id)}>
                      <SaveIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleEdit(doc)}>
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleDelete(doc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default HOADocumentsPage;
